/**
 * Handles the entire flow of a modifier dialog, including loading, opening/closing, etc.
 *
 * Injections:
 *   restaurantInfoFactory : to get if OO is disabled
 *   itemDetailsService : to get the details of the item to be displayed
 *   currentItemService : handles the current item being displayed at the time
 *   itemStatusChannel : to handle intents to open the modifier dialog
 */
export function ModifierDialogController(
    $animateCss,
    $document,
    $scope,
    ModalService,
    currentItemService,
    googleAnalyticsTrackingService,
    itemDetailsService,
    itemStatusChannel,
    restaurantInfoFactory
) {
    'ngInject';

    /**
     * Reference to the item model handled by the item service
     *
     * Although we reference the item here, it is only used to expose the object for the html to render.
     * Generally we want to keep the actual logic of manipulating/checking this object to the currentItemService.
     */
    $scope.curItem = null;

    $scope.curItemFlags = {
        // Flag to show/hide the spinner
        loading: true,
        // Flag to show the text indicating an item is being added to the cart
        addingToCart: false,
        open: false
    };

    // Flag to indicate whether the item can be added to cart
    $scope.canAddToCart = currentItemService.modifierGroupsSatisfied();

    $scope.itemClicked = itemClicked;
    $scope.itemEdited = itemEdited;
    $scope.closeDialog = closeDialog;
    $scope.decrementQuantity = currentItemService.decrementQuantity;
    $scope.incrementQuantity = currentItemService.incrementQuantity;
    $scope.singleSelectOptionClicked = currentItemService.singleSelectOptionClicked;
    $scope.multiSelectOptionClicked = currentItemService.multiSelectOptionClicked;
    $scope.updateParent = currentItemService.updateParent;
    $scope.cancelOpenedOption = cancelOpenedOption;
    $scope.incrementOptionQuantity = incrementOptionQuantity;
    $scope.decrementOptionQuantity = decrementOptionQuantity;
    $scope.clearModifierGroup = currentItemService.clearModifierGroup;
    $scope.getTotalItemPrice = currentItemService.getTotalItemPrice;
    $scope.addItemToCart = addItemToCart;

    $scope.modifierGroupSelectionEvent = googleAnalyticsTrackingService.getModifierGroupSelectionEvent();
    $scope.incrementQuantityEvent = googleAnalyticsTrackingService.getIncrementQuantityEvent();
    $scope.decrementQuantityEvent = googleAnalyticsTrackingService.getDecrementQuantityEvent();

    activate();

    ///---------

    function activate() {
        restaurantInfoFactory.getInfo(['onlineOrder'])
            .then(info => $scope.enableOO = info.onlineOrder.isOnlineOrderingEnabled);
    }

    /**
     * itemClicked(): populate the modifier dialog with an item's details based on the item passed in
     *  -> listener function for itemClicked event
     *
     *  @param group: need this to add to cart
     *  @param item: either use the id to retrieve the item details, or use its fields to initialize item details
     */
    function itemClicked(group, item) {
        let decorated = false;
        _openDialog(item);
        _setCurrentItem(group, item, decorated);
    }

    /**
     * itemEdited(): load the item passed in into the modifier dialog
     *  -> listener function for itemEdited event
     *
     *  @param cartItem: the object used to display on the shopping cart
     */
    function itemEdited(item) {
        let decorated = true;
        _openDialog(item);
        _setCurrentItem({
            idString: item.groupIdString,
            guid: item.groupGuid
        }, item, decorated);
    }

    function _setCurrentItem(group, item, decorated) {
        // If the cartItem was obtained from retrieving the cart from server, we will not have the itemDetails loaded
        const groupGuid = group.guid;
        const _getItemDetailFn = () => _getItemDetailByGuid(groupGuid, item, decorated);

        let itemDetail = _getItemDetailFn();
        if (!itemDetail) {
            itemDetailsService.setItemDetailByGuid(groupGuid, item)
                .then(() => {
                    let detail = _getItemDetailFn();
                    _createItem(group, detail);

                    _trackECProductDetail(detail, decorated);
                })
                .finally(() => $scope.curItemFlags.loading = false);

        } else {
            $scope.curItemFlags.loading = false;
            _createItem(group, itemDetail);

            _trackECProductDetail(itemDetail, decorated);
        }
    }

    function _trackECProductDetail(detail, isUpdateItem) {
        if (isUpdateItem) {
            return;
        }

        googleAnalyticsTrackingService.trackECProductDetail(detail);
    }

    function _getItemDetailByGuid(groupGuid, item, decorated) {
        if(decorated) {
            return itemDetailsService.getDecoratedItemDetailByGuid(item);
        }
        return itemDetailsService.getItemDetailByGuid(groupGuid, item.guid);
    }

    function _createItem(group, details) {
        currentItemService.createItem(group, details);
        $scope.curItem = currentItemService.getItem();
        $scope.curItemFlags.loading = false;
    }

    function _openDialog(item) {
        angular.element($document[0].querySelector('html')).addClass('noScroll');
        ModalService.modalShown();
        $scope.curItem = item;
        $scope.curItemFlags.open = true;
        $scope.curItemFlags.loading = true;
    }

    function closeDialog() {
        $scope.curItem = null;
        $scope.curItemFlags.open = false;
        $scope.curItemFlags.loading = false;
        $scope.curItemFlags.addingToCart = false;
        ModalService.modalHidden();
        angular.element($document[0].querySelector('html')).removeClass('noScroll');
    }

    function cancelOpenedOption($event, parentGroup, option) {
        // Prevent the click to trigger the checkbox
        $event.preventDefault();
        currentItemService.cancelOpenedOption(parentGroup, option);
    }

    function incrementOptionQuantity($event, parentGroup, option) {
        $event.preventDefault();
        currentItemService.incrementOptionQuantity(parentGroup, option);
    }

    function decrementOptionQuantity($event, parentGroup, option) {
        $event.preventDefault();
        currentItemService.decrementOptionQuantity(parentGroup, option);
    }

    /**
     * addItemToCart(): adds item to the cart
     *   -> closes the modifier dialog
     */
    function addItemToCart() {
        if ($scope.canAddToCart && !$scope.curItemFlags.loading) {
            $scope.curItemFlags.loading = true;
            $scope.curItemFlags.addingToCart = true;

            currentItemService.addItemToCart()
                .then(
                    () => {
                        $scope.closeDialog();

                        $animateCss(angular.element($document[0].querySelector('.t-badge')), {
                            keyframeStyle: 'badge .4s'
                        }).start();
                    }, $scope.closeDialog);
        }
    }

    // Create binding to update addToCart availability automatically
    $scope.$watch(currentItemService.modifierGroupsSatisfied, function(result) {
        $scope.canAddToCart = result;
    });

    itemStatusChannel.onSelectItem($scope, $scope.itemClicked);
    itemStatusChannel.onEditItem($scope, $scope.itemEdited);
}
